/**
 * viz — the GENERIC value gauge (L1). Pure inline SVG / DOM (the Flutter twin
 * is CustomPaint): no chart library, no canvas, no external font.
 *
 * `Gauge` is ONE component with three variants, and it used to be two
 * components with four. The straight one drew a bound value out of a bound max
 * as a bar or a strip of segments; the round one drew the same bound value out
 * of the same bound max as an arc — and declared the straight one as its own
 * `fallback`, which is the tell: a component whose graceful degradation is
 * another component with the same contract IS that component, drawn
 * differently. Shape is not identity, so the shape became a VARIANT
 * (`bar` · `segments` · `ring`) and the two entries became one.
 *
 * It is deliberately ROBUST: it absorbs the arithmetic a gauge always needs
 * (percent, clamp, over-threshold tone) so a COMPOSITION can stay pure
 * binding. That is the whole reason a domain ring (CaloryRing) can be authored
 * as data instead of native code — every computation it used to do in JS now
 * lives in here, generically.
 *
 * NO domain vocabulary: this draws `value` out of `max`. What the value counts
 * is the blueprint's business.
 */
import { num } from '../paths';
import { toneColor } from './ui';

const FULL = 360;

/** The tone the value has EARNED: the last threshold it is strictly past. */
function toneFor(value, thresholds, base) {
  if (!Array.isArray(thresholds)) return base;
  let tone = base;
  for (const t of thresholds) {
    if (!t || t.at == null || t.at === '') continue;
    const at = num(t.at);
    if (Number.isFinite(at) && value > at) tone = t.tone || tone;
  }
  return tone;
}

/**
 * The LINEAR readings — one filled `bar`, or a strip of `segments`.
 *
 * SEALED: the value is clamped to the track, so bad data can never draw past
 * the end of it.
 */
function LinearGauge({ variant, props, value: raw, max: maxRaw, tone, label }) {
  const segments = Math.max(1, Number(props.segments) || Number(maxRaw) || 3);
  const max = num(maxRaw) || segments;
  const value = Math.max(0, Math.min(max, num(raw)));
  const text = label || `${value} of ${max}`;

  if (variant === 'bar') {
    return (
      <div role="img" aria-label={text} style={{ height: 4, borderRadius: 'var(--r-pill)', background: 'var(--surface-3)', overflow: 'hidden' }}>
        <div style={{
          width: `${Math.round((value / (max || 1)) * 100)}%`, height: '100%', borderRadius: 'var(--r-pill)',
          background: toneColor(tone), transition: 'width 380ms var(--ease)',
        }} />
      </div>
    );
  }

  const filled = Math.round((value / (max || 1)) * segments);
  return (
    <span role="img" aria-label={text} style={{ display: 'inline-flex', gap: 2, alignItems: 'flex-end', height: 10 }}>
      {Array.from({ length: segments }).map((_, i) => (
        <span key={i} style={{
          width: 3, height: 3 + (i + 1) * 2, borderRadius: 1,
          background: i < filled ? toneColor(tone) : 'var(--surface-3)',
        }} />
      ))}
    </span>
  );
}

/**
 * Gauge — a bound value against a bound max, drawn `bar` · `segments` · `ring`.
 *
 * SEALED (all three): the value is CLAMPED to the track (bad data can never
 * draw more than a full gauge) and the over-threshold tone is a colour change,
 * never a clipped arc or an overflowing bar — the same honesty rule.
 *
 * Props: `value` · `max` (alias `target`) · `tone` · `label` (a11y) ·
 * `variant`; `segments` for the segmented strip; `thresholds:[{at,tone}]` ·
 * `startAngle` (default -90 = 12 o'clock) · `sweep` (default 360) · `cap` ·
 * `track` · `size` · `stroke` · `center` (a NODE prop: whatever the
 * composition wants inside the ring) for the radial one.
 *
 * `setValue` is a real slot: a connection can push a new value in without the
 * data lane re-deriving, exactly as BarChart's `setBars` does.
 *
 * A build that cannot draw an arc renders the BAR instead — the same reading,
 * straightened out. That fallback is INTERNAL rather than a registry
 * `fallback`, because it is one component degrading its own drawing, not one
 * component standing in for another.
 */
export function Gauge({ variant, props = {}, data, slots, nodeId, renderNode, children }) {
  const shape = props.variant || variant || 'segments';
  const pushed = slots?.setValue?.payload;
  const rawValue = pushed?.value !== undefined ? pushed.value
    : (props.value !== undefined ? props.value : data);
  const value = num(rawValue);
  const maxRaw = props.max !== undefined ? props.max : props.target;
  const max = maxRaw == null || maxRaw === '' ? null : num(maxRaw);
  const tone = toneFor(value, props.thresholds, props.tone || 'accent');
  const label = props.label
    || (max != null ? `${value} of ${max}` : String(rawValue == null ? '' : rawValue));

  if (shape !== 'ring') {
    return <LinearGauge variant={shape} props={props} value={rawValue} max={maxRaw} tone={tone} label={props.label} />;
  }

  const size = Number(props.size) || 140;
  const stroke = Number(props.stroke) || 12;
  const sweep = Math.min(FULL, Math.max(1, Number(props.sweep) || FULL));
  const startAngle = Number.isFinite(Number(props.startAngle)) ? Number(props.startAngle) : -90;

  const r = Math.max(1, (size - stroke) / 2);
  const circ = 2 * Math.PI * r;
  const arc = circ * (sweep / FULL);
  const pct = max ? Math.min(1, Math.max(0, value / max)) : 0;
  const c = size / 2;
  // The arc is drawn in its TONE, softening into the same tone's tint as it
  // comes round — one colour, two weights, so the ring reads as a sweep rather
  // than as a flat band. The gradient id is node-scoped because two rings on one
  // page sharing an id would both wear whichever was defined last.
  const gradientId = `viNativeRing-${nodeId || 'ring'}`;

  return (
    <div style={{ position: 'relative', width: size, height: size, flex: '0 0 auto' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={label}>
        <defs>
          {/* the SAME tone at two weights — never a second hue, so the arc
              still says exactly one thing about the value */}
          <linearGradient id={gradientId} gradientUnits="userSpaceOnUse" x1="0" y1={size} x2={size} y2="0">
            <stop offset="0%" stopColor={toneColor(tone)} stopOpacity="0.45" />
            <stop offset="100%" stopColor={toneColor(tone)} stopOpacity="1" />
          </linearGradient>
        </defs>
        {props.track === false ? null : (
          <circle
            cx={c} cy={c} r={r} fill="none" stroke="var(--surface-3)" strokeWidth={stroke}
            strokeDasharray={`${arc} ${circ}`}
            transform={`rotate(${startAngle} ${c} ${c})`}
          />
        )}
        {max ? (
          <circle
            cx={c} cy={c} r={r} fill="none"
            stroke={`url(#${gradientId})`} strokeWidth={stroke}
            strokeLinecap={props.cap === 'butt' ? 'butt' : 'round'}
            strokeDasharray={`${arc * pct} ${circ}`}
            transform={`rotate(${startAngle} ${c} ${c})`}
            style={{ transition: 'stroke-dasharray 420ms var(--ease)' }}
          />
        ) : null}
      </svg>
      {props.center || children ? (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', textAlign: 'center',
          padding: stroke, boxSizing: 'border-box', minWidth: 0,
        }}>
          {props.center ? renderNode?.(props.center, `${nodeId}__center`) : children}
        </div>
      ) : null}
    </div>
  );
}
